import { EmailRequestType, MessagingPlatform } from '@this+that/models';
import log from 'loglevel';

import { createApiWithAuth } from '../../utils/axios';

export interface ModifyEmailPayload {
  userEmailAddress: string;
  platform: MessagingPlatform;
  messageId: string;
  action: Extract<
    EmailRequestType,
    | EmailRequestType.ARCHIVE
    | EmailRequestType.INBOX
    | EmailRequestType.READ
    | EmailRequestType.UNREAD
    | EmailRequestType.SPAM
    | EmailRequestType.TRASH
    | EmailRequestType.MOVE_TO_FOLDER
    | EmailRequestType.STAR
    | EmailRequestType.UNSTAR
    | EmailRequestType.MARK_IMPORTANT
    | EmailRequestType.UNMARK_IMPORTANT
  >;
  folderId?: string;
}

export interface ModifyEmailLabelsUnifiedPayload {
  emailAddress: string;
  platform: MessagingPlatform;
  messageIds: string[];
  addLabelIds?: string[];
  removeLabelIds?: string[];
}

interface UnsubscribeFromEmailsParams {
  messageId: string;
}

let modifyEmailServiceApi: ReturnType<typeof createApiWithAuth> | null = null;

const getModifyEmailServiceApi = () => {
  if (!modifyEmailServiceApi) {
    const baseUrl = import.meta.env.VITE_USER_COMMUNICATION_BASE_URL;
    if (!baseUrl) {
      throw new Error('User Communication base URL is not set');
    }
    modifyEmailServiceApi = createApiWithAuth(baseUrl);
  }
  return modifyEmailServiceApi;
};

const handleRequestError = (context: string, error: unknown) => {
  log.error(`Modify Email Service Error [${context}]:`, error);
  throw error;
};

export const modifyEmail = async ({
  userEmailAddress,
  platform,
  messageId,
  action,
  folderId,
}: ModifyEmailPayload) => {
  try {
    const payload: Record<string, any> = {
      messageIds: [messageId],
      action,
    };

    if (platform === MessagingPlatform.GMAIL) {
      payload.gmailAddress = userEmailAddress;
    } else if (platform === MessagingPlatform.OUTLOOK) {
      payload.outlookAddress = userEmailAddress;
    } else {
      throw new Error(`Unsupported platform for modify email: ${platform}`);
    }

    if (action === EmailRequestType.MOVE_TO_FOLDER) {
      if (!folderId) {
        throw new Error('folderId is required for MOVE_TO_FOLDER action');
      }
      payload.folderId = folderId;
    }

    const { data } = await getModifyEmailServiceApi().post(
      '/modify-email',
      payload,
    );
    return data;
  } catch (error) {
    handleRequestError(`${action} email`, error);
  }
};

export const modifyEmailLabelsUnified = async ({
  emailAddress,
  platform,
  messageIds,
  addLabelIds = [],
  removeLabelIds = [],
}: ModifyEmailLabelsUnifiedPayload) => {
  const payload: Record<string, any> = {
    action: EmailRequestType.MODIFY_LABELS,
    removeLabelIds,
    addLabelIds,
    messageIds,
  };

  if (platform === MessagingPlatform.GMAIL) {
    payload.gmailAddress = emailAddress;
  } else if (platform === MessagingPlatform.OUTLOOK) {
    payload.outlookAddress = emailAddress;
  } else {
    throw new Error(`Unsupported platform for modify email: ${platform}`);
  }

  try {
    const { data } = await getModifyEmailServiceApi().post(
      '/modify-email',
      payload,
    );
    return data;
  } catch (error) {
    handleRequestError('modify labels unified', error);
  }
};

export const unsubscribeFromEmails = async ({
  messageId,
}: UnsubscribeFromEmailsParams) => {
  try {
    const payload: Record<string, any> = {
      messageId,
    };

    const { data } = await getModifyEmailServiceApi().post(
      '/unsubscribe-from-email',
      payload,
    );
    return data;
  } catch (error) {
    handleRequestError('unsubscribe from email', error);
  }
};
